<script setup>
  import {computed, inject, onBeforeMount, ref, watch} from "vue";

  import ComponentRowOriginFromMergeBadge from "@components/Badges/RowOriginFromMergeBadge.vue";
  import ComponentFakeInput from "@components/Inputs/FakeInput.vue";
  import ComponentFullDateInput from "@components/Inputs/FullDateInput.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {router, usePage} from "@inertiajs/vue3";
  import {MedButton, MedHoverCard, MedIcon, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({name: "SymptomTableRow"});

  const props = defineProps({
    isEditable: {
      type: Boolean,
      required: true,
    },
    symptom: {
      type: Object,
      required: true,
    },
    isDragging: {
      type: Boolean,
      required: true,
    },
  });

  const emit = defineEmits(["updateSymptoms"]);

  const page = usePage();

  const openDialog = inject("symptomOpenDialog");
  const deleteDialog = inject("recordEditDeleteRowDialog");

  const contextLabel = ref(null);
  const contextItems = ref([]);
  const symptomForm = ref(props.symptom);

  const isContextEditable = computed(() => {
    return props.symptom.context && props.symptom.context.length > 0;
  });

  const symptomaticContexts = {
    fam: "Medikation",
    diagnosis: "Erkrankung",
    laborvalue: "Laborwert",
  };

  watch(
    () => props.symptom,
    () => {
      symptomForm.value = props.symptom;
    },
    {deep: true},
  );

  watch(
    () => symptomForm.value?.context || symptomForm.value?.symptomcontexts.length,
    () => {
      buildContextLabel();
    },
    {
      deep: true,
    },
  );

  onBeforeMount(() => {
    buildContextLabel();
  });

  const buildContextLabel = () => {
    const message = [];

    if (symptomForm.value.context && symptomForm.value.contextvalue.length > 0) {
      symptomForm.value.contextvalue.map((contextId) => {
        switch (symptomForm.value.context) {
          case "fam": {
            const medication = page.props.medications?.data.find(
              (medication) => medication.abdata_key_fam === contextId,
            );
            if (medication) {
              message.push(medication.fam);
            }

            break;
          }
          case "diagnosis": {
            const disease = page.props.diseases?.data.find((disease) => disease.abdata_key_miv === contextId);
            if (disease) {
              message.push(disease.disease);
            }

            break;
          }
          case "laborvalue": {
            const laborValue = page.props.laborvalues?.data.find(
              (laborValue) => laborValue.mc_laborvalue_id === contextId,
            );
            if (laborValue) {
              message.push(laborValue.type);
            }

            break;
          }
          // No default
        }
      });
    }

    contextItems.value = message;

    contextLabel.value = message.join(", ").slice(0, 30);
    contextLabel.value += message.join(", ").length > 30 ? "..." : "";
  };

  const changeSince = (newValue) => {
    symptomForm.value.since = newValue;
    emit("updateSymptoms");
  };

  const putSymptom = (overrides = {}) => {
    router.put(
      route("symptoms.update", {
        patient: page.props.patient.id,
        record: page.props.record.id,
        symptom: symptomForm.value.id,
      }),
      {
        abdata_key_miv: symptomForm.value.abdata_key_miv,
        symptom: symptomForm.value.symptom,
        since: symptomForm.value.since,
        mc_symptom_id: symptomForm.value.mc_symptom_id,
        row: symptomForm.value.row,
        symptomcontexts: Array.isArray(symptomForm.value.symptomcontexts) ? symptomForm.value.symptomcontexts : [],
        ...overrides,
      },
      {
        preserveScroll: true,
        only: ["flash", "errors", "symptoms"],
      },
    );
  };

  const clearSince = () => {
    symptomForm.value.since = null;
    putSymptom({since: null});
  };

  const clearContext = () => {
    putSymptom({symptomcontexts: []});
  };

  const changeContext = (newValue) => {
    symptomForm.value.contextvalue = [];
    symptomForm.value.symptomcontexts = [];

    symptomForm.value.context = newValue;

    if (!newValue) {
      clearContext();
    }
  };

  const openContextDialog = () => {
    if (!symptomForm.value.context) return;

    openDialog("refSymptomContextDialog", symptomForm.value);
  };

  const showDeleteConfirmDialog = () => {
    deleteDialog({routeName: "symptoms", payload: {symptom: symptomForm.value.id}});
  };
</script>

<template>
  <li class="grid grid-cols-12 gap-x-6 py-2" :data-test="getTestId('row')">
    <!-- row 0 - optional origin when merging records -->
    <ComponentRowOriginFromMergeBadge :hashid="symptomForm.id" type="symptoms" class="col-span-12 ml-8" />

    <!-- col 1 - Symptom -->
    <div class="col-span-4">
      <div class="flex items-center">
        <template v-if="isEditable">
          <slot name="prefix">
            <div class="more icon-wrap flex items-center justify-center">
              <MedTooltip side="left" content="Zeile verschieben" :disabled="isDragging">
                <span
                  role="button"
                  tabindex="0"
                  aria-label="Zeile verschieben"
                  class="flex cursor-grab items-center active:cursor-grabbing"
                  drag-handle
                  @keydown.enter.prevent
                  @keydown.space.prevent
                >
                  <MedIcon size="lg" icon="drag_handle" />
                </span>
              </MedTooltip>
            </div>
          </slot>
        </template>

        <ComponentFakeInput
          class="ml-2 w-full"
          :value="symptomForm.symptom"
          :disabled="!isEditable"
          :test-id="getTestId('row-name')"
        />
      </div>
    </div>

    <!-- col 2 - Seit(Datum) -->
    <div class="col-span-2">
      <ComponentFullDateInput
        v-model="symptomForm.since"
        :disabled="!isEditable"
        :test-id="getTestId('row-since')"
        @input="changeSince"
        @clear="clearSince"
      />
    </div>

    <div class="col-span-2">
      <ComponentSelect
        ref="context"
        v-model="symptomForm.context"
        :options="symptomaticContexts"
        :disabled="!isEditable"
        :nullable="true"
        placeholder-text=""
        :test-id="getTestId('row-context')"
        @change="changeContext"
      />
    </div>

    <!-- col 4 - Warum/Was -->
    <div class="col-span-3">
      <div
        :class="cn('h-9 truncate border-b-1 border-gray-300 p-2 text-sm', {'border-dashed text-gray-500': !isEditable})"
        :data-test="getTestId('row-open-context-dialog')"
      >
        <template v-if="isContextEditable">
          <MedHoverCard v-if="contextItems.length > 0">
            <button
              type="button"
              :class="cn('w-full truncate text-left', isEditable ? 'cursor-pointer' : 'cursor-default')"
              :disabled="!isEditable"
              @click="openContextDialog"
            >
              {{ contextLabel }}
            </button>

            <template #content>
              <ul class="grid list-disc gap-1 pl-4 text-sm">
                <li v-for="(item, index) in contextItems" :key="index">{{ item }}</li>
              </ul>
            </template>
          </MedHoverCard>

          <button v-else class="size-full h-full cursor-pointer text-left text-mcred" @click="openContextDialog">
            Bitte auswählen
          </button>
        </template>

        <template v-else>
          <MedTooltip content="Bitte wählen Sie zuerst eine Vermutung aus.">
            <div class="size-full">-</div>
          </MedTooltip>
        </template>
      </div>
    </div>

    <div class="col-span-1 m-auto flex">
      <template v-if="isEditable">
        <MedTooltip content="Zeile löschen">
          <MedButton
            :aria-label="symptomForm.symptom ? `Symptom ${symptomForm.symptom} löschen` : 'Symptom löschen'"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('row-delete-button')"
            @click="showDeleteConfirmDialog"
          />
        </MedTooltip>
      </template>
    </div>
  </li>
</template>
